/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  ClientErrorHandler,
  ClientMessageErrorHandler,
  ClientMessageHandler
} from './types';
import WorkerClientLogger from './WorkerClientLogger';

/**
 * Defines the configuration options to be used when initializing an AbstractWorker
 * implementation.
 */
export interface AbstractWorkerConfig {
  context?: string;
  onUpdate: ClientMessageHandler;
  onUpdateFailure?: ClientMessageErrorHandler
  onError?: ClientErrorHandler;
};

/**
 * The AbstractWorker provides the abstraction from which all Worker Implementations
 * are to extend in order to provide common behaviors and state. This includes the
 * management of listeners, incoming messages, errors, termination, and logging.
 *
 * ```typescript
 *
 *  class SomeWorker extends AbstractWorker {
 *    start({onUpdate, onUpdateFailure, onError}: SomeWorkerConfig) {
 *      super.initialize(new Worker(new URL('./some.worker.ts', import.meta.url)), {
 *        context: 'SomeWorker',
 *        onUpdate,
 *        onUpdateFailure,
 *        onError
 *      });
 *    }
 *  }
 *  export default new SomeWorker();
 * ```
 */
class AbstractWorker {
  /**
   * Defines a reference to the underlying Web Worker which is to be managed.
   * The Web Worker instance must be provided directly in order to be resolved
   * contextually in scope.
   */
  protected worker: Worker;

  /**
   * Provides a reference to the client configuration options provided to the
   * initialize method.
   */
  protected configuration: AbstractWorkerConfig | null;

  /**
   * Defines a reference to the WorkerClientLogger API which is used for logging
   * all incoming messages and errors.
   */
  protected logger: WorkerClientLogger;

  /**
   * The initialize method is to be invoked when a client wishes to initialize
   * the underlying Worker Script. This method requires the Web Worker instance
   * (due to script access constraints).
   *
   * Additionally, clients should provide parameters for the AbstractWorkerConfig
   * in order to be informed of incoming messages and errors.
   *
   * ```typescript
   *
   *  class SomeWorker extends AbstractWorker {
   *    start({onUpdate, onUpdateFailure, onError}: SomeWorkerConfig) {
   *      this.initialize(new Worker(new URL('./some.worker.ts', import.meta.url)), {
   *        context: 'SomeWorker',
   *        onUpdate,
   *        onUpdateFailure,
   *        onError
   *      });
   *    }
   *  }
   *  export default new SomeWorker();
   * ```
   */
  public initialize = (worker: Worker, config: AbstractWorkerConfig) => {
    if (!this.worker) {
      this.logger        = new WorkerClientLogger(config.context);
      this.worker        = worker;
      this.configuration = config;
      this.worker.addEventListener('message', this.onMessage);
      this.worker.addEventListener('messageerror', this.onMessageError);
      this.worker.addEventListener('error', this.onError);
      this.logger.onInitialize();
    }
  };

  /**
   * Provides a convenience proxy for the `postMessage` invocation against the underlying
   * WebWorker instance. This method should be used in order to facilitate separation
   * between the type of message being sent, and the data which is to be sent, as well
   * as to ensure logging of all messages.
   *
   * ```typescript
   *  onSomeChange(account: Account, user: User) {
   *    this.send(SomeWorkerMessage.SOME_CHANGE, account, user);
   *  }
   * ```
   *
   * @param type A string providing the type of message that is being sent. This value
   *        is generally used by the worker script to determine which action should
   *        take place.
   * @param data Arbitrary parameters to be passed to the worker script.
   */
  public send = (type, ...data) => {
    this.logger.onMessageSent();
    this.worker.postMessage([type, ...data]);
  };

  /**
   * Provides a utility for determining if the AbstractWorker instance is currently
   * active.
   *
   * ```typescript
   *  if (!SomeWorker.isActive) {
   *    SomeWorker.start({
   *      onUpdate : items => {
   *        // handle updates ...
   *      },
   *      onUpdateFailure : items => {
   *        // handle update failures ...
   *      },
   *      onError : items => {
   *        // handle errors ...
   *      }
   *    });
   *  }
   * ```
   *
   * @returns true if the AbstractWorker is currently active (i.e. the underlying
   *          WebWorker has been instantiated and has not been terminated.), otherwise,
   *          false.
   */
  get isActive(): boolean {
    return !!this.worker;
  }

  /**
   * Handles incoming messages from the underlying WebWorker script, invoking the
   * appropriate logger and onUpdate configuration option.
   */
  protected onMessage = ({data}: MessageEvent) => {
    this.logger.onMessageReceived();
    this.configuration?.onUpdate?.([...data]);
  };

  /**
   * Handles incoming message failures from the underlying WebWorker script, invoking
   * the appropriate logger and onUpdateFailure configuration option (if provided).
   */
  protected onMessageError = (event: MessageEvent) => {
    this.logger.onMessageError(event);
    this.configuration?.onUpdateFailure?.(event);
  };

  /**
   * Handles errors from the underlying WebWorker script, invoking the appropriate
   * logger and onError configuration option (if provided).
   */
  protected onError = (event: ErrorEvent) => {
    this.logger.onError(event);
    this.configuration?.onError?.(event);
  };

  /**
   * Allows for terminating the AbstractWorker API and underlying Worker instance.
   * Note, invoking this method effectively nullifies Alert Notification short-polling
   * and requires re-invoking AbstractWorker.start to re-start polling.
   *
   * ```typescript
   *   SomeWorker.destruct();
   * ```
   */
  public destruct = () => {
    if (this.worker) {
      this.worker.terminate();
      this.worker.removeEventListener('message', this.onMessage);
      this.worker.removeEventListener('messageerror', this.onMessageError);
      this.worker.removeEventListener('error', this.onError);
      this.logger.onDestruct();
      this.worker        = this.logger = null;
      this.configuration = null;
    }
  };
}

export default AbstractWorker;
